import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";

type Terminal = {
  id: string;
  operating_mode?: string;
  store_id?: string;
  pos_id?: string;
};
type Saved = {
  id: string;
  terminal_id: string;
  nombre: string;
  store_id: string | null;
  pos_id: string | null;
  activo: boolean;
};
type Operation = {
  id: string;
  venta_id: string | null;
  plan_nombre?: string | null;
  monto_membresia?: number | null;
  terminal_id: string;
  mp_order_id: string | null;
  estado: string;
  estado_detalle: string | null;
  mp_payment_id: string | null;
  created_at: string;
  ventas_pos?: { numero_venta: number; total: number; estado: string } | null;
};
const money = (n: number) =>
  new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(n || 0);
const activeStates = [
  "creando",
  "created",
  "at_terminal",
  "action_required",
  "pending",
  "processing",
  "procesando",
];
const badge = (s: string) =>
  s === "processed"
    ? "bg-green-100 text-green-700"
    : activeStates.includes(s)
      ? "bg-amber-100 text-amber-700"
      : "bg-gray-100 text-gray-700";

export default function PointOperations({
  gimnasioId,
  sucursalId,
  onMessage,
}: {
  gimnasioId?: string;
  sucursalId?: string;
  onMessage: (v: string) => void;
}) {
  const [saved, setSaved] = useState<Saved | null>(null),
    [terminals, setTerminals] = useState<Terminal[]>([]),
    [operations, setOperations] = useState<Operation[]>([]),
    [busy, setBusy] = useState(false),
    [remoteMode, setRemoteMode] = useState<string | null>(null);
  const invoke = async (body: Record<string, unknown>) =>
    supabase.functions.invoke("mercadopago-point", {
      body: {
        ...body,
        gimnasio_id: gimnasioId,
        sucursal_id: sucursalId,
        modo: "production",
      },
    });
  const showError = async (error: any) => {
    try {
      const payload = await error?.context?.json();
      onMessage(
        payload?.error ||
          error?.message ||
          "No se pudo completar la operación.",
      );
    } catch {
      onMessage(error?.message || "No se pudo completar la operación.");
    }
  };
  const loadLocal = async () => {
    if (!gimnasioId || !sucursalId) return;
    const [{ data: terminal }, { data: ops, error }] = await Promise.all([
      supabase
        .from("mercadopago_point_terminales")
        .select("id,terminal_id,nombre,store_id,pos_id,activo")
        .eq("gimnasio_id", gimnasioId)
        .eq("sucursal_id", sucursalId)
        .eq("modo", "production")
        .eq("activo", true)
        .limit(1)
        .maybeSingle(),
      invoke({ action: "operations" }),
    ]);
    setSaved(terminal as Saved | null);
    if (error) onMessage(error.message);
    else setOperations((ops?.orders || []) as Operation[]);
  };
  useEffect(() => {
    void loadLocal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gimnasioId, sucursalId]);
  const discover = async () => {
    setBusy(true);
    const { data, error } = await invoke({ action: "terminals" });
    setBusy(false);
    if (error) return void (await showError(error));
    const list = (data?.terminals || []) as Terminal[];
    setTerminals(list);
    const match = list.find((t) => t.id === saved?.terminal_id);
    setRemoteMode(match?.operating_mode || null);
    if (!list.length)
      onMessage("No se encontraron terminales asociadas a la cuenta.");
  };
  const link = async (t: Terminal) => {
    setBusy(true);
    const { error } = await invoke({
      action: "save_terminal",
      terminal_id: t.id,
      nombre: `Point ${t.id.split("__").pop()}`,
      store_id: t.store_id || null,
      pos_id: t.pos_id || null,
    });
    setBusy(false);
    if (error) return void (await showError(error));
    onMessage(
      "Terminal vinculada a la sucursal. Reiníciala y confirma que indique modo PDV.",
    );
    setRemoteMode(t.operating_mode || null);
    await loadLocal();
  };
  const activatePdv = async (t: Terminal) => {
    setBusy(true);
    const { error } = await invoke({
      action: "configure_pdv",
      terminal_id: t.id,
    });
    setBusy(false);
    if (error) return void (await showError(error));
    onMessage(
      "Modo PDV solicitado. Reinicia la terminal y vuelve a consultar para confirmar el cambio.",
    );
    await discover();
  };
  const disablePdv = async (t: Terminal) => {
    if (
      !window.confirm(
        "La terminal volverá al modo normal y dejará de recibir cobros desde este sistema. ¿Continuar?",
      )
    )
      return;
    setBusy(true);
    const { error } = await invoke({
      action: "disable_pdv",
      terminal_id: t.id,
    });
    setBusy(false);
    if (error) return void (await showError(error));
    setSaved(null);
    setRemoteMode("STANDALONE");
    onMessage(
      "Modo PDV desactivado. Reinicia la terminal para volver a usarla normalmente.",
    );
    await discover();
    await loadLocal();
  };
  const reconcile = async () => {
    setBusy(true);
    const { data, error } = await invoke({ action: "reconcile" });
    setBusy(false);
    if (error) return void (await showError(error));
    const failed = (data?.results || []).filter(
      (r: { synced: boolean }) => !r.synced,
    ).length;
    onMessage(
      failed
        ? `Conciliación terminada con ${failed} operación(es) sin respuesta. No se alteró su estado.`
        : "Operaciones conciliadas con Mercado Pago.",
    );
    await loadLocal();
  };
  const cancel = async (order: Operation) => {
    setBusy(true);
    const { error } = await invoke({ action: "cancel", order_id: order.id });
    setBusy(false);
    if (error) return void (await showError(error));
    onMessage("Cancelación confirmada por Mercado Pago.");
    await loadLocal();
  };
  return (
    <div className="space-y-6">
      <section className="bg-white border rounded-2xl p-6">
        <div className="flex flex-wrap justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold">Mercado Pago Point</h2>
            <p className="text-gray-500 mt-1">
              Configuración y estado verificable de la terminal física.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={discover}
              disabled={busy}
              className="border rounded-xl px-4 py-3 font-bold"
            >
              Buscar terminales
            </button>
            <button
              onClick={reconcile}
              disabled={busy}
              className="bg-[#27481f] text-white rounded-xl px-4 py-3 font-bold"
            >
              Conciliar pendientes
            </button>
          </div>
        </div>
        {saved ? (
          <div className="mt-6 grid md:grid-cols-[1fr_auto] gap-4 bg-green-50 border border-green-200 rounded-xl p-5">
            <div>
              <p className="font-bold text-green-800">Terminal configurada</p>
              <p className="mt-1">{saved.nombre}</p>
              <p className="text-xs text-gray-500 break-all mt-1">
                {saved.terminal_id}
              </p>
            </div>
            <div className="md:text-right">
              <p className="text-sm text-gray-500">
                Modo confirmado en última consulta
              </p>
              <p
                className={`font-bold ${remoteMode === "PDV" ? "text-green-700" : "text-amber-700"}`}
              >
                {remoteMode || "Aún no consultado"}
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800">
            No hay una terminal configurada para esta sucursal.
          </div>
        )}{" "}
        {terminals.length > 0 && (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-6">
            {terminals.map((t) => (
              <div key={t.id} className="border rounded-xl p-4">
                <div className="flex justify-between gap-3">
                  <p className="font-bold">{t.id.split("__").pop()}</p>
                  <span
                    className={`text-xs font-bold px-2 py-1 rounded-full ${t.operating_mode === "PDV" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}
                  >
                    {t.operating_mode || "UNDEFINED"}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-2 break-all">{t.id}</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <button
                    onClick={() => link(t)}
                    disabled={busy || saved?.terminal_id === t.id}
                    className="border border-[#27481f] text-[#27481f] rounded-lg px-4 py-2 font-semibold disabled:opacity-40"
                  >
                    {saved?.terminal_id === t.id
                      ? "Terminal seleccionada"
                      : "Usar en esta sucursal"}
                  </button>
                  {t.operating_mode !== "PDV" && (
                    <button
                      onClick={() => activatePdv(t)}
                      disabled={busy}
                      className="bg-[#27481f] text-white rounded-lg px-4 py-2 font-semibold"
                    >
                      Activar PDV
                    </button>
                  )}
                  {t.operating_mode === "PDV" && (
                    <button
                      onClick={() => disablePdv(t)}
                      disabled={busy}
                      className="border border-red-300 text-red-700 rounded-lg px-4 py-2 font-semibold"
                    >
                      Desactivar PDV
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      <section className="bg-white border rounded-2xl overflow-hidden">
        <div className="p-5 flex justify-between">
          <div>
            <h2 className="text-xl font-bold">Últimas operaciones</h2>
            <p className="text-gray-500 text-sm mt-1">
              El estado se actualiza mediante webhook o conciliación manual.
            </p>
          </div>
          <button
            onClick={() => void loadLocal()}
            className="border rounded-lg px-3 py-2 h-fit"
          >
            <i className="ri-refresh-line" />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="p-4">Fecha</th>
                <th className="p-4">Venta</th>
                <th className="p-4">Orden</th>
                <th className="p-4">Monto</th>
                <th className="p-4">Estado</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {operations.map((o) => (
                <tr key={o.id} className="border-t">
                  <td className="p-4 whitespace-nowrap">
                    {new Date(o.created_at).toLocaleString("es-CL")}
                  </td>
                  <td className="p-4 font-bold">
                    {o.ventas_pos?.numero_venta
                      ? `VTA-${String(o.ventas_pos.numero_venta).padStart(6, "0")}`
                      : o.plan_nombre ? `Membresía · ${o.plan_nombre}` : "—"}
                  </td>
                  <td className="p-4 font-mono text-xs">
                    {o.mp_order_id || "Creando…"}
                  </td>
                  <td className="p-4">{money(o.ventas_pos?.total || o.monto_membresia || 0)}</td>
                  <td className="p-4">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-bold ${badge(o.estado)}`}
                    >
                      {o.estado}
                    </span>
                    {o.estado_detalle && (
                      <p className="text-xs text-gray-400 mt-1">
                        {o.estado_detalle}
                      </p>
                    )}
                  </td>
                  <td className="p-4">
                    {["created", "at_terminal"].includes(o.estado) && (
                      <button
                        onClick={() => cancel(o)}
                        disabled={busy}
                        className="text-red-600 border border-red-200 rounded-lg px-3 py-2 font-semibold"
                      >
                        Cancelar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!operations.length && (
                <tr>
                  <td colSpan={6} className="p-12 text-center text-gray-400">
                    No hay operaciones Point registradas.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
